import { ArrowUpRight, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SocialIcon } from '@/components/ui/SocialIcon';
import { useI18n } from '@/store/preferences';
import type { SocialLink } from '@/lib/types';

interface FooterProps {
  name: string;
  email: string;
  socialLinks: SocialLink[];
}

export function Footer({ name, email, socialLinks }: FooterProps) {
  const { t, locale } = useI18n();
  const year = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden border-t border-line">
      {/* Faint accent bloom anchoring the bottom of the page. */}
      <div
        className="pointer-events-none absolute inset-x-0 -top-px h-px"
        style={{
          background:
            'linear-gradient(to right, transparent, rgb(var(--c-accent) / 0.55), transparent)',
        }}
        aria-hidden
      />

      <div className="shell py-20 sm:py-24">
        <div className="grid gap-14 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-20">
          <div className="max-w-xl">
            <p className="eyebrow mb-5">
              <span className="h-px w-6 bg-line" aria-hidden />
              {t.footer.cta}
            </p>

            <h2 className="h2 mb-8 text-balance">{t.footer.headline}</h2>

            <div className="flex flex-wrap items-center gap-3">
              <Link to="/contact" className="btn-accent group">
                {t.nav.contact}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
              <EmailButton email={email} copyLabel={t.footer.copy} copiedLabel={t.footer.copied} />
            </div>
          </div>

          <div className="flex flex-col gap-10 sm:flex-row sm:gap-16 lg:gap-20">
            <nav aria-label="Footer">
              <p className="eyebrow mb-4">{locale === 'es' ? 'Secciones' : 'Pages'}</p>
              <ul className="space-y-2.5">
                {(['projects', 'about', 'certificates', 'contact'] as const).map((key) => (
                  <li key={key}>
                    <Link
                      to={`/${key}`}
                      className="text-sm text-muted transition-colors hover:text-ink"
                    >
                      {t.nav[key]}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <div>
              <p className="eyebrow mb-4">{locale === 'es' ? 'Enlaces' : 'Elsewhere'}</p>
              <ul className="space-y-2.5">
                {socialLinks.map((link) => (
                  <li key={link.id}>
                    <a
                      href={link.url}
                      target={link.url.startsWith('mailto:') ? undefined : '_blank'}
                      rel="noopener noreferrer"
                      className="group inline-flex items-center gap-2.5 text-sm text-muted transition-colors hover:text-ink"
                    >
                      <SocialIcon iconKey={link.iconKey} className="h-4 w-4" />
                      {link.label}
                      <ArrowUpRight className="h-3 w-3 opacity-0 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:opacity-100" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="rule my-12" />

        <div className="flex flex-col-reverse gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-mono text-2xs tracking-wide text-faint">
            © {year} {name}
          </p>
          <p className="font-mono text-2xs tracking-wide text-faint">
            {t.footer.builtWith}
          </p>
        </div>
      </div>
    </footer>
  );
}

/**
 * The address is behind a copy action rather than printed raw: it keeps the
 * footer clean, and copying is what a recruiter actually wants to do with it.
 */
function EmailButton({
  email,
  copyLabel,
  copiedLabel,
}: {
  email: string;
  copyLabel: string;
  copiedLabel: string;
}) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (insecure context or denied): the mailto link below
      // is still there, so there is nothing useful to report.
    }
  }

  return (
    <span className="inline-flex items-stretch overflow-hidden rounded-full border border-line">
      <a
        href={`mailto:${email}`}
        className="px-4 py-2.5 font-mono text-xs text-muted transition-colors hover:bg-elevated hover:text-ink"
      >
        {email}
      </a>
      <button
        type="button"
        onClick={handleCopy}
        className="border-l border-line px-3 text-muted transition-colors hover:bg-elevated hover:text-ink"
        aria-label={copied ? copiedLabel : copyLabel}
        title={copied ? copiedLabel : copyLabel}
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-emerald-400" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </button>
    </span>
  );
}
